import React, { useState, useEffect, useRef } from "react";
import { X, ChevronRight, AlertTriangle } from "lucide-react";

export function MultiClientWarningDialog({ jobs, onCancel, onConfirm }) {
  const groups = jobs.reduce((acc, j) => {
    const key = j.client_email || "sconosciuto";
    if (!acc[key]) acc[key] = { email: key, name: j.company || j.client_name || key, trackings: [] };
    acc[key].trackings.push(j.tracking_number);
    return acc;
  }, {});
  const groupList = Object.values(groups);

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="multi-client-warning-dialog">
      <div className="bg-white rounded-lg w-full max-w-sm overflow-hidden">
        <div className="bg-amber-500 px-5 py-4 text-white flex items-center gap-3">
          <AlertTriangle size={28} className="flex-shrink-0" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-90">Attenzione</p>
            <p className="text-lg font-bold leading-tight">Clienti diversi</p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-zinc-700">
            Stai per firmare <b>{jobs.length} consegne</b> di <b>{groupList.length} clienti diversi</b> con la stessa firma. Sei sicuro?
          </p>
          <div className="bg-zinc-50 border border-zinc-200 rounded p-2 max-h-40 overflow-auto space-y-1.5">
            {groupList.map((g) => (
              <div key={g.email} className="text-xs">
                <p className="font-bold text-[#09090B] truncate">{g.name}</p>
                <p className="text-[10px] text-zinc-500 font-mono">{g.trackings.join(" \u00b7 ")}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onCancel} className="flex-1 h-11 border-2 border-zinc-200 text-zinc-600 font-bold uppercase tracking-wide rounded-sm text-xs" data-testid="multi-client-cancel-btn">
              Annulla
            </button>
            <button onClick={onConfirm} className="flex-1 h-11 bg-amber-500 text-white font-bold uppercase tracking-wide rounded-sm text-xs" data-testid="multi-client-confirm-btn">
              Continua comunque
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShiftConfirmDialog({ event, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="shift-confirm-dialog">
      <div className="bg-white rounded-lg w-full max-w-sm overflow-hidden">
        <div className={`${event.color} px-5 py-6 text-white text-center`}>
          <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Conferma azione</p>
          <p className="text-2xl font-bold uppercase tracking-tight">{event.label}</p>
        </div>
        <div className="p-5">
          <p className="text-sm text-zinc-600 text-center mb-5 leading-relaxed">{event.desc}</p>
          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 h-12 border-2 border-zinc-200 text-zinc-600 font-bold uppercase tracking-wide rounded-sm text-sm" data-testid="shift-cancel-btn">
              Annulla
            </button>
            <button onClick={onConfirm} className={`flex-1 h-12 ${event.color} text-white font-bold uppercase tracking-wide rounded-sm text-sm`} data-testid="shift-confirm-btn">
              Conferma
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SlideToConfirm({ label, onConfirm, onCancel }) {
  const [dragX, setDragX] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const trackRef = useRef(null);
  const startXRef = useRef(null);
  const HANDLE = 56;

  useEffect(() => {
    if (trackRef.current) setTrackWidth(trackRef.current.offsetWidth);
  }, []);

  const onStart = (clientX) => { startXRef.current = clientX - dragX; };
  const onMove = (clientX) => {
    if (startXRef.current === null) return;
    const max = trackWidth - HANDLE;
    setDragX(Math.max(0, Math.min(max, clientX - startXRef.current)));
  };
  const onEnd = () => {
    if (startXRef.current === null) return;
    startXRef.current = null;
    const max = trackWidth - HANDLE;
    if (dragX >= max - 8) onConfirm();
    else setDragX(0);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-end sm:items-center justify-center p-4" data-testid="slide-confirm">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <p className="text-xs font-bold uppercase tracking-widest text-red-600 text-center mb-2">Conferma irreversibile</p>
        <p className="text-2xl font-bold text-center text-[#09090B] mb-6">FINE TURNO</p>
        <div ref={trackRef}
          className="relative h-16 bg-red-100 rounded-full overflow-hidden border-2 border-red-200 select-none"
          onMouseMove={(e) => onMove(e.clientX)}
          onMouseUp={onEnd}
          onMouseLeave={onEnd}
          onTouchMove={(e) => onMove(e.touches[0].clientX)}
          onTouchEnd={onEnd}
        >
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-sm font-bold uppercase tracking-wide text-red-700/70">{label}</span>
          </div>
          <div className="absolute top-0 left-0 h-full bg-red-500 transition-none"
            style={{ width: `${dragX + HANDLE}px`, opacity: 0.25 }} />
          <div
            onMouseDown={(e) => onStart(e.clientX)}
            onTouchStart={(e) => onStart(e.touches[0].clientX)}
            className="absolute top-1 left-1 h-[52px] w-[52px] bg-red-600 text-white rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg"
            style={{ transform: `translateX(${dragX}px)` }}
            data-testid="slide-handle"
          >
            <ChevronRight size={22} strokeWidth={3} />
            <ChevronRight size={22} strokeWidth={3} className="-ml-3" />
          </div>
        </div>
        <button onClick={onCancel} className="mt-5 w-full h-11 border-2 border-zinc-200 text-zinc-600 font-bold uppercase tracking-wide rounded-sm text-sm" data-testid="slide-cancel-btn">
          Annulla
        </button>
      </div>
    </div>
  );
}

export function PushHelpModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-sm w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="push-help-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Attiva le notifiche</h3>
          <button onClick={onClose}><X size={20} className="text-zinc-400" /></button>
        </div>
        <p className="text-sm text-zinc-600">Le notifiche sono bloccate. Per attivarle:</p>
        <div className="bg-zinc-50 rounded p-3">
          <p className="text-xs font-bold uppercase text-[#FF5200] mb-2">Chrome (Android)</p>
          <ol className="text-xs text-zinc-700 space-y-1 list-decimal list-inside">
            <li>Tocca l&apos;<b>icona lucchetto</b> nella barra indirizzi</li>
            <li>Tocca <b>Autorizzazioni</b></li>
            <li>Cambia <b>Notifiche</b> in &quot;Consenti&quot;</li>
            <li>Ricarica la pagina</li>
          </ol>
        </div>
        <button onClick={() => { onClose(); window.location.reload(); }} className="w-full h-10 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm">Ho sbloccato, ricarica</button>
      </div>
    </div>
  );
}
